import React, { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Breadcrumb from "../../../components/common/breadcrumb";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { RoomChangeReportApi } from "../../../api/ipd/ipdReportsAPI";

const RoomChangeReport = () => {

    const [filteredData, setFilteredData] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [search, setSearch] = useState("");

    const dispatch = useDispatch();

    const fetchData = async () => {

        dispatch({ type: "LOADER_START" });

        try {
            const requestData = {
                fromDate,
                toDate,
                search,
            };

            const response = await RoomChangeReportApi(requestData);

            if (response.success) {
                showToast("success", response.message);

                const formattedData = response.data.map((item, index) => ({
                    index: index + 1,
                    admitDate: item.ipdEntryId?.date?.split("T")[0] || "",
                    ipdNo: item.ipdEntryId?.ipdNo || "",
                    uhidNo: item.ipdEntryId?.uhidNo || "",

                    patientName:
                        (item.ipdEntryId?.patientNamePrefix ? item.ipdEntryId?.patientNamePrefix + " " : "") +
                        (item.ipdEntryId?.patientName || ""),

                    sex: item.ipdEntryId?.sex || "",
                    age: item.ipdEntryId?.age ? `${item.ipdEntryId?.age} ${item.ipdEntryId?.ageSuffix || ""}` : "",
                    mobileNo: item.ipdEntryId?.mobileNo || "",
                    address: item.ipdEntryId?.address || "",
                    gueguardianName: item.ipdEntryId?.guardianName || "",

                    preciousRoomNo: item?.roomId?.roomNo || "",
                    newRoomNo: item?.newRoomId?.roomNo || "",

                    changeDate: item.newDate?.split("T")[0] || "",
                    changeTime: item.newTime || "",

                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));

                setFilteredData(formattedData);

            } else {
                showToast("error", response.message);
            }

        } catch (error) {
            showToast("error", error.message);

        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    // =============================
    // TABLE HEADERS
    // =============================
    const headers = [
        { label: "S.No", key: "index" },
        { label: "Admit Date", key: "admitDate" },
        { label: "IPD No", key: "ipdNo" },
        { label: "UHID No", key: "uhidNo" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Age", key: "age" },
        { label: "Mobile No", key: "mobileNo" },
        { label: "Address", key: "address" },
        { label: "Guardian Name", key: "gueguardianName" },
        { label: "Old Room No", key: "preciousRoomNo" },
        { label: "New Room No", key: "newRoomNo" },
        { label: "Change Date", key: "changeDate" },
        { label: "Change Time", key: "changeTime" },
        { label: "Created By", key: "createdBy" },
        { label: "Created Date", key: "createdAt" },
        { label: "Updated By", key: "updatedBy" },
        { label: "Updated Date", key: "updatedAt" },
    ];

    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        wrap: true,
        width: "160px",
    }));

    // =============================
    // EXPORT EXCEL
    // =============================
    const exportToExcel = () => {
        const sheetData = filteredData.map((row) => {
            let obj = {};
            headers.forEach((h) => (obj[h.label] = row[h.key]));
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Room Change Report");
        XLSX.writeFile(workbook, "Room_Change_Report.xlsx");
    };

    // =============================
    // EXPORT PDF
    // =============================
    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.text("Room Change Report", 14, 10);

        const tableColumn = headers.map((h) => h.label);
        const tableRows = filteredData.map((row) => headers.map((h) => row[h.key]));

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            styles: { cellWidth: "wrap" },
        });

        doc.save("Room_Change_Report.pdf");
    };

    // =============================
    // RESET FILTERS
    // =============================
    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setSearch("");
        setFilteredData([]);
    };

    return (
        <Fragment>
            <Breadcrumb parent="IPD" title="Room Change Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>Room Change Report</h5>

                                <div className="row mt-3">
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-3">
                                        <label>Search</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search..."
                                        />
                                    </div>

                                    <div className="col-sm mt-4">
                                        <button
                                            className="btn btn-primary me-2"
                                            onClick={fetchData}
                                        >
                                            Search
                                        </button>
                                        <button
                                            className="btn btn-warning"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    <div className="col-sm mt-4 text-end">
                                        <button
                                            className="btn btn-success me-2"
                                            onClick={exportToExcel}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    pagination
                                    highlightOnHover
                                    dense
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default RoomChangeReport;
